declare global {
  interface Window {
    ProductCardSwatches?: {
      initCard: (card: Element, options?: object) => void;
      initAll: (root?: ParentNode, options?: object) => void;
    };
  }
}

const SELECTOR = "[data-tw-collection-filter-grid]";
const MOBILE_MAX_WIDTH_MEDIA = "(max-width: 767px)";

type SwiperHost = HTMLElement & { swiper?: { update: () => void } };

function normalizeMatchString(value: string) {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function getFilterTerms(value: string) {
  return value
    .split(",")
    .map((term) => normalizeMatchString(term))
    .filter((term) => term !== "");
}

function scrollActiveFilterChipIntoView(filterBtns: HTMLElement[], activeFilter: string | null) {
  if (activeFilter === null || !window.matchMedia(MOBILE_MAX_WIDTH_MEDIA).matches) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const target = filterBtns.find((btn) => {
        const word = normalizeMatchString(btn.dataset.filter || "");
        return word !== "" && word === activeFilter;
      });
      target?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    });
  });
}

function initCollectionFilterGrid(root: ParentNode = document) {
  const sections = root.querySelectorAll<HTMLElement>(SELECTOR);
  for (const section of Array.from(sections)) {
    if (section.dataset.initialized === "true") continue;
    section.dataset.initialized = "true";
    setupSection(section);
  }
}

function setupFilterArrows(section: HTMLElement, sectionId: string) {
  if (section.dataset.filterArrows !== "true") return;

  const scroller = section.querySelector<HTMLElement>("[data-filter-scroll]");
  const leftBtn = section.querySelector<HTMLButtonElement>(`#filter-arrow-left-${sectionId}`);
  const rightBtn = section.querySelector<HTMLButtonElement>(`#filter-arrow-right-${sectionId}`);
  if (!scroller || !leftBtn || !rightBtn) return;

  const isMobile = () => window.matchMedia(MOBILE_MAX_WIDTH_MEDIA).matches;

  const setVisible = (btn: HTMLButtonElement, visible: boolean) => {
    btn.style.display = visible && isMobile() ? "flex" : "none";
  };

  const update = () => {
    if (!isMobile()) {
      setVisible(leftBtn, false);
      setVisible(rightBtn, false);
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const canScroll = maxScroll > 2;
    setVisible(leftBtn, canScroll && scroller.scrollLeft > 2);
    setVisible(rightBtn, canScroll && scroller.scrollLeft < maxScroll - 2);
  };

  const scrollByAmount = (dir: -1 | 1) => {
    scroller.scrollBy({ left: dir * Math.round(scroller.clientWidth * 0.8), behavior: "smooth" });
  };

  leftBtn.addEventListener("click", () => scrollByAmount(-1));
  rightBtn.addEventListener("click", () => scrollByAmount(1));
  scroller.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);
  update();
}

function setupSection(section: HTMLElement) {
  const sectionId = section.dataset.sectionId || "";
  const pageSize = parseInt(section.dataset.pageSize || "10", 10);
  const grid = section.querySelector<HTMLElement>(`#tw-filter-grid-${sectionId}`);
  const filterBtns = Array.from(section.querySelectorAll<HTMLElement>("[data-filter]"));
  const filterChips = Array.from(section.querySelectorAll<HTMLElement>("[data-filter-chip]"));
  const clearBtns = Array.from(section.querySelectorAll<HTMLButtonElement>("[data-clear-filter]"));
  const loadMoreContainer = section.querySelector<HTMLElement>("[data-load-more-container]");
  const loadMoreBtn = section.querySelector<HTMLButtonElement>("[data-load-more]");

  if (!grid) return;

  setupFilterArrows(section, sectionId);

  let activeFilter: string | null = null;
  let visibleCount = pageSize;
  let nextPageUrl = loadMoreBtn?.dataset.url || null;
  let loadingMore = false;

  const getAllItems = () => Array.from(grid.querySelectorAll<HTMLElement>("[data-grid-item]"));

  const initVisibleCards = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.ProductCardSwatches?.initAll(grid);
        for (const card of Array.from(grid.querySelectorAll(".product-card"))) {
          const wrap = card.closest("[data-grid-item]");
          if (!wrap || wrap.classList.contains("hidden")) continue;
          const imgEl = card.querySelector(".product-image-swiper") as SwiperHost | null;
          const swEl = card.querySelector(".swatch-swiper") as SwiperHost | null;
          try {
            imgEl?.swiper?.update();
          } catch {}
          try {
            swEl?.swiper?.update();
          } catch {}
        }
      });
    });
  };

  const fetchNextPage = async () => {
    if (!nextPageUrl || loadingMore) return false;

    loadingMore = true;
    try {
      const separator = nextPageUrl.includes("?") ? "&" : "?";
      const response = await fetch(`${nextPageUrl}${separator}section_id=${sectionId}`);
      if (!response.ok) throw new Error("Failed to load more products");

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const newGrid = doc.querySelector<HTMLElement>(`#tw-filter-grid-${sectionId}`);
      if (newGrid) {
        for (const item of Array.from(newGrid.children)) {
          grid.appendChild(item);
        }
      }

      const newBtn = doc.querySelector<HTMLButtonElement>(`[data-section-id="${sectionId}"][data-load-more]`);
      nextPageUrl = newBtn?.dataset.url || null;
      if (!nextPageUrl && loadMoreBtn) {
        loadMoreBtn.removeAttribute("data-url");
      } else if (loadMoreBtn && nextPageUrl) {
        loadMoreBtn.dataset.url = nextPageUrl;
      }

      return Boolean(newGrid);
    } finally {
      loadingMore = false;
    }
  };

  // ponytail: filter loads remaining pages on demand; upgrade path is a server-side filter endpoint
  const ensureAllProductsLoaded = async () => {
    while (nextPageUrl) {
      await fetchNextPage();
    }
  };

  const render = () => {
    const allItems = getAllItems();
    const isFiltered = activeFilter !== null;
    const filterTerms = activeFilter ? getFilterTerms(activeFilter) : [];

    const matching: HTMLElement[] = [];
    for (const item of allItems) {
      const isBanner = item.hasAttribute("data-clp-banner");
      if (isFiltered) {
        if (isBanner) {
          item.classList.add("hidden");
          continue;
        }
        const title = normalizeMatchString(item.dataset.productTitle || "");
        const isMatch = filterTerms.some((term) => title.includes(term));
        if (isMatch) {
          matching.push(item);
        } else {
          item.classList.add("hidden");
        }
      } else {
        matching.push(item);
      }
    }

    for (const item of matching) {
      item.classList.add("hidden");
    }

    // ponytail: mood tiles count toward page size (10 slots = products + banners)
    const toShow = matching.slice(0, visibleCount);
    for (const item of toShow) {
      item.classList.remove("hidden");
    }

    initVisibleCards();

    if (loadMoreContainer) {
      const hasMoreInDom = matching.length > visibleCount;
      const hasMoreOnServer = Boolean(nextPageUrl);
      loadMoreContainer.classList.toggle("hidden", !hasMoreInDom && !hasMoreOnServer);
    }
  };

  const updateActiveState = () => {
    for (const chip of filterChips) {
      const chipFilterBtn = chip.querySelector<HTMLElement>("[data-filter]");
      const word = normalizeMatchString(chipFilterBtn?.dataset.filter || "");
      const isActive = word !== "" && word === activeFilter;

      const clearBtn = chip.querySelector<HTMLElement>("[data-clear-filter]");
      if (clearBtn) {
        clearBtn.classList.toggle("hidden", !isActive);
        clearBtn.classList.toggle("flex", isActive);
      }
    }

    for (const btn of filterBtns) {
      const word = normalizeMatchString(btn.dataset.filter || "");
      if (word === "") continue;
      const isActive = word === activeFilter;
      const label = btn.querySelector("span:last-child");
      label?.classList.toggle("font-semibold", isActive);
      label?.classList.toggle("text-black", isActive);
      label?.classList.toggle("text-secondaryhighlight", !isActive);
    }
  };

  for (const btn of filterBtns) {
    btn.addEventListener("click", async () => {
      const word = normalizeMatchString(btn.dataset.filter || "");
      if (word === "") {
        activeFilter = null;
      } else if (activeFilter === word) {
        activeFilter = null;
      } else {
        activeFilter = word;
      }
      visibleCount = pageSize;

      if (activeFilter !== null) {
        if (loadMoreBtn) {
          loadMoreBtn.disabled = true;
        }
        try {
          await ensureAllProductsLoaded();
        } finally {
          if (loadMoreBtn) {
            loadMoreBtn.disabled = false;
          }
        }
      }

      render();
      updateActiveState();
      scrollActiveFilterChipIntoView(filterBtns, activeFilter);
    });
  }

  for (const btn of clearBtns) {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      activeFilter = null;
      visibleCount = pageSize;
      render();
      updateActiveState();
    });
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      visibleCount += pageSize;

      let itemCount = getAllItems().length;
      if (itemCount < visibleCount && nextPageUrl) {
        loadMoreBtn.disabled = true;
        const originalText = loadMoreBtn.textContent || "";
        loadMoreBtn.textContent = "Lädt...";
        try {
          while (itemCount < visibleCount && nextPageUrl) {
            await fetchNextPage();
            itemCount = getAllItems().length;
          }
        } finally {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = originalText;
        }
      }

      render();
    });
  }

  render();
  updateActiveState();
}

export { initCollectionFilterGrid };
